import { Box, Button, Center, Flex, Text } from '@chakra-ui/react'
import { Loading } from '@components'
import { GearIcon, Mic } from '@components/icons'
import { AudioCaptureProvider } from '@context'
import { useAudioCapture, useExperienceManager, useWidget } from '@hooks'
import { formatTimeFromSeconds } from '@utilities'
import { useStoreState } from 'easy-peasy'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  useSessionControllerGetSessionRecap,
  useSessionControllerPostDiscardSession
} from '~/clinician-api'
import { useRecordingDuration } from '../../sessions/components/telehealth/hooks/use-recording-time'
import { AudioSettings } from './audio-settings'
import { EndSession } from './end-session'
import { AudioLevel } from './select-mic'

export const Activity = ({ recap }: { recap: any }) => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const [showSettings, setShowSettings] = useState(false)
  const [showEndSession, setShowEndSession] = useState(false)
  const {
    startRecording,
    toggleMute,
    isMuted,
    disableEchoCancelation,
    startContentShare,
    stopRecording,
    isRecording
  } = useAudioCapture()
  const { isSafari } = useExperienceManager()
  // @ts-ignore
  const { user } = useStoreState(state => state.auth)
  const { settings } = useWidget()
  const { mutateAsync: discardSession } =
    useSessionControllerPostDiscardSession()
  const history = useHistory()

  const {
    recordingDuration,
    beginRecordingTimer,
    pauseRecordingTimer,
    resumeRecordingTimer
  } = useRecordingDuration({
    recordingStartedAt: recap?.recordingStartedAt
  })

  useEffect(() => {
    if (isRecording) {
      // eslint-disable-next-line
      parent.postMessage(
        { type: 'BP_SESSION_RECORDING_STARTED', sessionId },
        '*'
      )
    }
  }, [isRecording])

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === 'BP_DISCARD_SESSION') {
        stopRecording()
        await discardSession({ id: sessionId })
        history.push('/mini-widget/start-session')
      }

      if (event.data.type === 'BP_SHOW_END_SESSION') {
        setShowEndSession(true)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  useEffect(() => {
    if (recap)
      startRecording({
        isTelehealth: Boolean(recap.isTelehealth),
        isUsingHeadphones: Boolean(recap.isUsingHeadphones),
        noteType: user?.organization?.default_note_type || 'soap',
        telehealthAudioSource: recap.telehealthAudioSource,
        isHighQualityAudio: Boolean(recap.isHighQualityAudio)
      })
    beginRecordingTimer()

    if (recap.isTelehealth && recap.isUsingHeadphones) {
      startContentShare()
    }
  }, [recap])

  useEffect(() => {
    if (isMuted) {
      pauseRecordingTimer()
    } else {
      resumeRecordingTimer()
    }
  }, [isMuted])

  useEffect(() => {
    if (recap?.isTelehealth && !isSafari) {
      disableEchoCancelation(true)
    } else {
      disableEchoCancelation(false)
    }
  }, [recap?.isTelehealth, isSafari])

  if (showSettings) return <AudioSettings setShowSettings={setShowSettings} />
  if (showEndSession) return <EndSession user={user} />

  return (
    <Flex direction="column" justifyContent="space-between" height="100%">
      <Box>
        <Flex justifyContent="space-between">
          <Text>{isMuted ? 'Recording paused' : 'Recording in progress'}</Text>
          <Text>{formatTimeFromSeconds(recordingDuration)}</Text>
        </Flex>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          h="40px"
          pr="xsmall"
        >
          <Box mr="xsmall">
            <Mic />
          </Box>
          <AudioLevel />
        </Flex>
      </Box>
      {!isMuted && (
        <Box>
          <Flex justifyContent="space-between">
            <Flex
              onClick={() => setShowSettings(true)}
              _hover={{ cursor: 'pointer' }}
              justifyContent="center"
              alignItems="center"
              borderRadius="24px"
              w="40px"
              h="40px"
              border="1px solid #373C4123"
              p="xsmall"
            >
              <GearIcon />
            </Flex>
            <Button
              mt="auto"
              backgroundColor={
                settings.minifiedViewSettings?.buttons
                  ?.primaryBackgroundColor ?? 'primary'
              }
              color={
                settings.minifiedViewSettings?.buttons?.primaryTextColor ??
                'white'
              }
              borderRadius={
                settings.minifiedViewSettings?.buttons?.borderRadius ?? '24px'
              }
              borderColor={
                settings.minifiedViewSettings?.buttons?.primaryBorderColor ??
                'primary'
              }
              borderWidth={
                settings.minifiedViewSettings?.buttons?.primaryBorderWidth ??
                '1px'
              }
              onClick={() => {
                // eslint-disable-next-line
                parent.postMessage(
                  {
                    type: 'BP_SESSION_RECORDING_PAUSED',
                    sessionId
                  },
                  '*'
                )
                toggleMute()
              }}
              data-click-text="Stop Recording"
            >
              Stop Recording
            </Button>
          </Flex>
        </Box>
      )}
      {isMuted && (
        <Box w="100%">
          <Button
            w="100%"
            backgroundColor={
              settings.minifiedViewSettings?.buttons
                ?.secondaryBackgroundColor ?? 'primary'
            }
            color={
              settings.minifiedViewSettings?.buttons?.secondaryTextColor ??
              'white'
            }
            borderRadius={
              settings.minifiedViewSettings?.buttons?.borderRadius ?? '24px'
            }
            borderColor={
              settings.minifiedViewSettings?.buttons?.secondaryBorderColor ??
              'primary'
            }
            borderWidth={
              settings.minifiedViewSettings?.buttons?.secondaryBorderWidth ??
              '1px'
            }
            onClick={() => {
              toggleMute()
              // eslint-disable-next-line
              parent.postMessage(
                {
                  type: 'BP_SESSION_RECORDING_RESUMED',
                  sessionId
                },
                '*'
              )
            }}
            data-click-text="Resume Recording"
          >
            Resume Recording
          </Button>
          <Button
            onClick={() => {
              setShowEndSession(true)
              // eslint-disable-next-line
              parent.postMessage(
                {
                  type: 'BP_SESSION_RECORDING_ENDED',
                  sessionId
                },
                '*'
              )
            }}
            w="100%"
            backgroundColor={
              settings.minifiedViewSettings?.buttons?.primaryBackgroundColor ??
              'primary'
            }
            color={
              settings.minifiedViewSettings?.buttons?.primaryTextColor ??
              'white'
            }
            borderRadius={
              settings.minifiedViewSettings?.buttons?.borderRadius ?? '24px'
            }
            borderColor={
              settings.minifiedViewSettings?.buttons?.primaryBorderColor ??
              'primary'
            }
            borderWidth={
              settings.minifiedViewSettings?.buttons?.primaryBorderWidth ??
              '1px'
            }
            data-click-text="End Session"
          >
            End Session
          </Button>
        </Box>
      )}
    </Flex>
  )
}

export const SessionActivity = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { settings } = useWidget()

  const {
    data: recap,
    isLoading: isLoadingSessionRecap,
    refetch: refetchRecap
  } = useSessionControllerGetSessionRecap(sessionId, {
    query: {
      enabled: !!sessionId
    }
  })

  if (isLoadingSessionRecap)
    return (
      <Center h="100%">
        <Loading
          color={
            settings?.minifiedViewSettings?.audioIndicatorColor ?? 'primary'
          }
        />
      </Center>
    )

  return (
    <AudioCaptureProvider sessionId={sessionId}>
      <Box
        id="bp-extension-loaded"
        bg={
          settings.minifiedViewSettings?.container?.backgroundColor ?? 'white'
        }
        display="flex"
        flexDirection="column"
        height="100%"
        width="100%"
        p="small"
      >
        <Activity recap={recap} />
      </Box>
    </AudioCaptureProvider>
  )
}
